import {
    Localization,
    LocalizedDistance,
    TenantVertical,
    UserProfileId,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { AgentDirectorySearchBar } from "components/agent-directory/agent-directory-search-bar";
import { AgentDirectoryPickerList } from "components/agent-directory/agent-directory-search-list";
import { verticalLabels } from "components/agent-directory/vertical-labels";
import { WhiteLabelRestricted } from "components/app/white-label-restricted";
import { useLocalizedStrings } from "components/locale-provider/locale-provider";
import { trpc } from "components/rpc/trpc";
import { SearchRequestPagination } from "components/simple-search/search-request-pagination";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import {
    useGetUserProfileQuery,
    UserProfileStatus,
    useSearchUserProfilesQuery,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { ResponsiveMode } from "store/reducers/responsive";
import { useRegionId } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import {
    AgentDirectoryFilter,
    AgentDirectoryState,
    getInitialAgentDirectoryState,
} from "store/slices/agent-directory";
import {
    ClientTransferSubject,
    TransferSearchLocation,
    verticalForSubject,
} from "./transfer-types";

export const mapThreshold: ResponsiveMode = ResponsiveMode.xLarge;

interface SelectAgentForClientTransferProps {
    subject: ClientTransferSubject;
    location: TransferSearchLocation | null;
    onTransferred: () => void;
}

const getInitialState = (
    location: TransferSearchLocation | null,
    locale: Localization,
    verticals: TenantVertical[],
) => {
    const initialState = getInitialAgentDirectoryState();

    if (!location) {
        return {
            ...initialState,
            searchFilter: { ...initialState.searchFilter, verticals },
            searchRequest: { ...initialState.searchRequest, verticals },
        };
    }

    const geoParams = {
        address: location.address,
        city: location.city,
        zip: location.zip,
        county: "",
        state: location.state,
        matchType: "distance",
        distance: Math.ceil(locale.conversions.toMeters(50 as LocalizedDistance)),
    };

    return {
        ...initialState,
        searchFilter: {
            ...initialState.searchFilter,
            geoParams,
            verticals,
        },
        searchRequest: {
            ...initialState.searchRequest,
            geoParams,
            verticals,
        },
    };
};

export const SelectAgentForClientTransfer: React.FC<
    SelectAgentForClientTransferProps
> = ({ subject, location, onTransferred }) => {
    const publicProfileEnabled = useTenantSettings().settings.enablePublicProfile;
    const locale = useLocalizedStrings();
    const requiredVerticals = useMemo(() => [verticalForSubject(subject)], [subject]);
    const initialState = getInitialState(location, locale, requiredVerticals);
    const trpcContext = trpc.useTRPC();

    const [newAppState, setNewAppState] = useState<AgentDirectoryState>(initialState);
    const regionId = useRegionId();

    const userProfileQuery = useGetUserProfileQuery(
        {},
        { enabled: publicProfileEnabled, keepPreviousData: true },
    );

    const userProfileComplete = useMemo(() => {
        if (!userProfileQuery.data) {
            return false;
        }

        return (
            userProfileQuery.data.getUserProfile.status === UserProfileStatus.Public &&
            userProfileQuery.data.getUserProfile.isComplete
        );
    }, [userProfileQuery.data]);

    const displayMode = useMemo(() => {
        if (!publicProfileEnabled) {
            return "none";
        }

        if (userProfileQuery.isLoading) {
            return "none";
        }

        return userProfileComplete ? "full" : "profile-incomplete";
    }, [publicProfileEnabled, userProfileComplete, userProfileQuery.isLoading]);

    const searchQuery = useSearchUserProfilesQuery(
        {
            params: {
                ...newAppState.searchRequest,
                regionId,
            },
        },
        {
            enabled: displayMode === "full" || displayMode === "profile-incomplete",
        },
    );

    const onSearchUpdated = useCallback(
        (search: AgentDirectoryFilter) => {
            const scoped = { ...search, verticals: requiredVerticals };
            setNewAppState(prev => ({
                ...prev,
                searchFilter: scoped,
                searchRequest: {
                    ...prev.searchRequest,
                    query: scoped.search,
                    geoParams: scoped.geoParams,
                    verticals: scoped.verticals,
                },
            }));
        },
        [requiredVerticals],
    );

    const handlePageChange = useCallback((page: number) => {
        setNewAppState(prev => ({
            ...prev,
            searchRequest: { ...prev.searchRequest, page },
        }));
    }, []);

    const handlePageSizeChange = useCallback((pageSize: number) => {
        setNewAppState(prev => ({
            ...prev,
            searchRequest: { ...prev.searchRequest, perPage: pageSize },
        }));
    }, []);

    const agentHits = useMemo(() => {
        return searchQuery.data?.searchUserProfiles.hits || [];
    }, [searchQuery.data?.searchUserProfiles.hits]);

    const [selectedAgent, setSelectedAgent] = React.useState<UserProfileId | null>(null);

    const [note, setNote] = React.useState<string>("");

    const showNoMatchingAdvisors =
        searchQuery.isSuccess && agentHits.length === 0 && !selectedAgent;

    const requestClientTransfer = useMutation(
        trpcContext.clientTransfer.request.mutationOptions(),
    );

    const handleTransferClient = useCallback(() => {
        requestClientTransfer.mutate(
            {
                subject,
                toUserProfileId: selectedAgent,
                note,
            },
            { onSuccess: () => onTransferred() },
        );
    }, [requestClientTransfer, subject, selectedAgent, note, onTransferred]);

    return (
        <WhiteLabelRestricted>
            {displayMode === "none" ? null : displayMode === "full" ||
              displayMode === "profile-incomplete" ? (
                <>
                    {!selectedAgent ? (
                        <AgentDirectorySearchBar
                            value={newAppState.searchFilter}
                            onChange={onSearchUpdated}
                            verticalFilter="locked"
                            locationDropdown="left"
                        />
                    ) : null}

                    <div className="mt-2 lg:mt-4">
                        <div className="w-full flex">
                            <div className="full-width">
                                {showNoMatchingAdvisors ? (
                                    <InlineBanner type="info">
                                        No{" "}
                                        {verticalLabels[
                                            requiredVerticals[0]
                                        ].toLowerCase()}{" "}
                                        advisors found in this area. Try widening the
                                        search area or clearing the search text.
                                    </InlineBanner>
                                ) : null}
                                <AgentDirectoryPickerList
                                    agents={agentHits}
                                    value={selectedAgent}
                                    onChange={setSelectedAgent}
                                />
                                {searchQuery.data && !selectedAgent ? (
                                    <SearchRequestPagination
                                        request={newAppState.searchRequest}
                                        total={searchQuery.data.searchUserProfiles.total}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                    />
                                ) : null}
                                {selectedAgent ? (
                                    <div className="mt-4">
                                        <TextArea
                                            label="Add a note"
                                            rows={5}
                                            value={note}
                                            onChange={e => setNote(e.target.value)}
                                        />

                                        <div className="flex items-center  space-x-2">
                                            <PrimaryButton
                                                disabled={requestClientTransfer.isPending}
                                                onClick={handleTransferClient}>
                                                Transfer client
                                            </PrimaryButton>
                                            {requestClientTransfer.isPending ? (
                                                <Spinner />
                                            ) : null}
                                        </div>
                                        {requestClientTransfer.isError ? (
                                            <div className="mt-2">
                                                <InlineBanner type="error">
                                                    {requestClientTransfer.error.message}
                                                </InlineBanner>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </>
            ) : null}
        </WhiteLabelRestricted>
    );
};
